import { Button } from "components/DesignSystem/Button/Button";
import { GreenTickGreenCircleGradient } from "components/icons/GreenTickGreenCircleGradient";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useGetAllTransactionsDataQuery } from "store/apis/transactions";
import Loader from "components/DesignSystem/Loader/loader";
import { Divider } from "components/design/Divider";
import { formatDate } from "utils/formatDate";

type DateParams = {
  start_date: string;
  end_date: string;
};

type Props = {
  dateParams: DateParams;
  onClose: () => void;
  onViewUnreviewed: () => void;
};

export const SmartReviewAllTabEmptyState = ({
  dateParams,
  onClose,
  onViewUnreviewed,
}: Props) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data, isLoading } = useGetAllTransactionsDataQuery({
    groupId,
    entityId,
    startDate: { value: dateParams.start_date },
    endDate: { value: dateParams.end_date },
    notReviewed: { value: true },
    pageSize: "1",
  });

  const unreviewedCount = data?.total_count ?? 0;

  if (isLoading) return <Loader />;

  return (
    <div className="t-flex t-flex-col t-items-center t-justify-center t-h-full t-py-16 t-gap-5">
      <GreenTickGreenCircleGradient className="t-size-16" />
      <p className="t-text-subtext t-text-text-100 t-mb-1 t-text-center">
        Categories for all transactions have been confirmed.
      </p>

      {unreviewedCount > 0 && (
        <div className="t-flex t-flex-col t-items-center t-gap-4 t-w-full t-max-w-3xl">
          <Divider classNames="t-w-full">
            <span className="t-text-body-sm t-text-neutral-30 t-whitespace-nowrap">
              Suggested next steps
            </span>
          </Divider>

          <p className="t-text-body-lg t-text-text-60 t-text-center t-mb-0">
            There are {unreviewedCount} additional transactions from{" "}
            {formatDate(dateParams.start_date)} –{" "}
            {formatDate(dateParams.end_date)} that require manual review for the
            category assigned. Review and confirm to stay up to date.
          </p>

          <div className="t-flex t-gap-3">
            <Button customType="secondary" onClick={onClose}>
              Do it later
            </Button>
            <Button customType="primary" onClick={onViewUnreviewed}>
              View unreviewed transactions
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
